<template>
  <!-- Q2 "Current tasks" from the Work Progress design: the open tasks needing attention today,
       split into three exclusive buckets, with a stacked bar and one flag underneath -->
  <v-card class="current-card">
    <div class="d-flex justify-space-between align-baseline flex-wrap ga-2">
      <h2 class="card-title">{{ $t('taskOverview.currentTasks') }}</h2>
      <span v-if="overview" class="text-caption text-medium-emphasis">{{ $t('taskOverview.oneBucket') }}</span>
    </div>

    <v-skeleton-loader v-if="!overview && loading" type="article" />
    <div v-else-if="!overview" class="text-body-2 text-medium-emphasis">
      {{ $t(isTaskCrossFiltered ? 'taskOverview.noMatch' : 'taskSCurve.noData') }}
    </div>

    <template v-else>
      <div class="buckets" :style="{ '--bucket-count': items.length }">
        <div class="total">
          <span class="stat-label">{{ $t('taskOverview.total') }}</span>
          <span class="stat-big">{{ total.toLocaleString() }}</span>
        </div>
        <div
          v-for="item in items"
          :key="item.key"
          class="bucket bucket--clickable"
          :class="{ 'is-selected': selected === item.key, 'is-dimmed': selected && selected !== item.key }"
          :style="{ borderLeftColor: item.color }"
          role="button"
          tabindex="0"
          :aria-pressed="selected === item.key"
          @click="toggleStatusFilter(item.key)"
          @keydown.enter.prevent="toggleStatusFilter(item.key)"
          @keydown.space.prevent="toggleStatusFilter(item.key)"
        >
          <span class="bucket-n" :class="item.numCls">{{ item.n.toLocaleString() }}</span>
          <span class="bucket-label">{{ item.label }}</span>
          <span class="bucket-sub">{{ item.sub }}</span>
        </div>
      </div>

      <div class="stack">
        <div
          v-for="item in items"
          :key="item.key"
          :style="{ width: `${item.pct}%`, background: item.color }"
        ></div>
      </div>

      <div
        class="flag flag--clickable"
        :class="{ 'is-selected': selected === flag.key, 'is-dimmed': selected && selected !== flag.key }"
        role="button"
        tabindex="0"
        :aria-pressed="selected === flag.key"
        @click="toggleStatusFilter(flag.key)"
        @keydown.enter.prevent="toggleStatusFilter(flag.key)"
      >
        <span class="flag-value" :class="flag.cls">{{ flag.value.toLocaleString() }}</span>
        <span class="flag-label">{{ flag.label }}</span>
        <span class="text-caption text-medium-emphasis">· {{ flag.sub }}</span>
      </div>
    </template>
  </v-card>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { taskSCurveFilters, toggleStatusFilter, isTaskCrossFiltered } from '@/utils/taskSCurveFilters';

const props = defineProps({
  overview: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();
const isLite = computed(() => props.overview?.mode === 'lite');

// Clicking a bucket (or the flag) cross-filters the other visuals by that status
const selected = computed(() => taskSCurveFilters.statuses[0] ?? null);

// Bar colours are the same in both themes; the numbers use the theme-aware text tokens
const BAR = { info: '#2196F3', warn: '#F5A623', err: '#E53935', mute: '#5C6B7A' };

const source = computed(() => {
  const o = props.overview;
  if (!o) return [];
  if (isLite.value) {
    // Lite: no planned dates, so "in progress" splits by whether it was updated in the last week
    return [
      { key: 'inProgress', label: t('taskStatus.inProgress'), sub: t('taskStatus.updatedThisWeek'), n: o.inProgress - o.stalled, color: BAR.info },
      { key: 'stalled', label: t('taskStatus.stalled'), sub: t('taskStatus.stalledHint'), n: o.stalled, color: BAR.warn, numCls: 'tk-warn' },
      { key: 'notStarted', label: t('taskStatus.notStarted'), sub: t('taskStatus.notStartedHint'), n: o.notStarted, color: BAR.mute },
    ];
  }
  // Sync: every open task that has started or should have is in exactly one of these
  return [
    { key: 'onSchedule', label: t('taskStatus.inProgress'), sub: t('taskStatus.onSchedule'), n: o.onSchedule, color: BAR.info },
    { key: 'behindSchedule', label: t('taskStatus.behindSchedule'), sub: t('taskStatus.behindScheduleHint'), n: o.behindSchedule, color: BAR.warn, numCls: 'tk-warn' },
    { key: 'overdue', label: t('taskStatus.overdue'), sub: t('taskStatus.overdueHint'), n: o.overdue, color: BAR.err, numCls: 'tk-err' },
  ];
});

const total = computed(() => source.value.reduce((sum, item) => sum + item.n, 0));

const items = computed(() => source.value.map(item => ({
  ...item,
  pct: total.value ? Math.round(item.n / total.value * 100) : 0,
  // Amber / red only when there is something to worry about
  numCls: item.n > 0 ? item.numCls : undefined,
})));

// Below the bar: what's coming next. The design shows "Blocked" here, but the task API has no
// prerequisite data, so the sync view shows the tasks that start after today instead.
const flag = computed(() => {
  const o = props.overview;
  if (!o) return { key: null, value: 0, label: '', sub: '' };
  if (isLite.value) {
    // Lite: completions in the last 7 days against the 7 days before
    const delta = o.completedThisWeek - o.completedLastWeek;
    return {
      key: 'completed',
      value: o.completedThisWeek,
      label: t('taskOverview.completedThisWeek'),
      sub: t('taskOverview.vsLastWeek', { n: `${delta >= 0 ? '+' : ''}${delta}` }),
      cls: 'tk-ok',
    };
  }
  return { key: 'upcoming', value: o.upcoming, label: t('taskStatus.upcoming'), sub: t('taskStatus.upcomingHint') };
});
</script>

<style scoped>
.current-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 16px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.buckets {
  display: grid;
  grid-template-columns: auto repeat(var(--bucket-count, 3), minmax(0, 1fr));
  gap: 16px;
  align-items: end;
}
.total {
  display: flex;
  flex-direction: column;
  padding-right: 16px;
  border-right: 1px solid var(--tk-divider);
}
.stat-label {
  font-size: 13px;
  opacity: 0.7;
}
.stat-big {
  font-size: 46px;
  line-height: 50px;
  font-variant-numeric: tabular-nums;
}
.bucket {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  border-left: 4px solid;
  padding-left: 10px;
}
.bucket--clickable,
.flag--clickable {
  cursor: pointer;
  border-radius: 4px;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}
.bucket--clickable:hover,
.flag--clickable:hover {
  background: var(--tk-row-hover);
}
.is-selected {
  background: var(--tk-row-hover);
  outline: 1px solid var(--tk-info);
  outline-offset: 2px;
}
.is-dimmed {
  opacity: 0.45;
}
.bucket-n {
  font-size: 32px;
  line-height: 36px;
  font-variant-numeric: tabular-nums;
}
.bucket-label {
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bucket-sub {
  font-size: 11px;
  opacity: 0.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stack {
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: var(--tk-track);
}
.stack > div {
  transition: width 0.4s ease;
}
.flag {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--tk-divider);
  margin-top: auto;
}
.flag-value {
  font-size: 24px;
  line-height: 28px;
  font-variant-numeric: tabular-nums;
}
.flag-label {
  font-size: 14px;
}
@media (max-width: 560px) {
  .buckets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .total {
    grid-column: 1 / -1;
    border-right: 0;
    padding-right: 0;
  }
}
</style>
